import React, { useEffect, useState, useMemo } from "react";
import Layout from "@/components/Layout";
import ModalCloseBtn from "@/components/ModalCloseBtn";
import { Users, Search, Filter, Download, Upload, User as UserIcon, CheckCircle2, AlertCircle, X, ExternalLink, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import api, { BACKEND_URL } from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/lib/auth.jsx";
import { getNav } from "@/pages/navConfigs";

export default function MemberDirectoryPage() {
  const { user, customRole } = useAuth();
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [openMember, setOpenMember] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/members", { params: search ? { search } : {} });
      setMembers(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search]);

  const filtered = useMemo(() =>
    (roleFilter ? members.filter((m) => m.role === roleFilter) : members)
      .slice().sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [members, roleFilter]);

  return (
    <Layout navItems={getNav(user.role, customRole)}>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="member-directory-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl brand-gradient flex items-center justify-center text-white"><Users className="w-6 h-6" /></div>
          <div>
            <h1 className="font-display font-black text-2xl text-slate-900">Member Directory</h1>
            <p className="text-sm text-slate-500">Every member's HR profile — passport photo, Aadhaar, CV, tenure. Downloadable as PDF.</p>
          </div>
        </div>

        <div className="dashboard-card p-4 mb-4 flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[220px] relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input placeholder="Search name / email / member ID / phone / city" className="input-pill pl-9" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="member-search" />
          </div>
          <select className="input-pill w-56" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} data-testid="member-role-filter">
            <option value="">All roles</option>
            {[...new Set(members.map((m) => m.role))].sort().map((r) => (
              <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
            ))}
          </select>
          <span className="text-xs text-slate-400">{filtered.length} member{filtered.length === 1 ? "" : "s"}</span>
        </div>

        {loading ? (
          <div className="dashboard-card p-12 text-center text-slate-400">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="dashboard-card p-12 text-center text-slate-400">No matching members.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((m) => (
              <button key={m.user_id} onClick={() => setOpenMember(m)} className="dashboard-card p-4 text-left hover:border-orange-300 transition-colors" data-testid={`member-card-${m.user_id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-14 h-14 rounded-2xl overflow-hidden bg-slate-100 flex items-center justify-center shrink-0">
                    {m.photo_url ? <img src={m.photo_url.startsWith("http") ? m.photo_url : `${BACKEND_URL}${m.photo_url}`} alt="" className="w-full h-full object-cover" /> :
                      <UserIcon className="w-6 h-6 text-slate-300" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-900 truncate">{m.name}</div>
                    <div className="text-[10px] px-2 py-0.5 inline-block rounded-full bg-orange-50 text-orange-700 mt-0.5">{ROLE_LABELS[m.role] || m.role}</div>
                    <div className="text-xs text-slate-500 mt-1 truncate">{m.email}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">{m.member_id || "No Member ID"} · {m.city || "—"}</div>
                    <div className="mt-1.5">
                      {m.profile_updated_at
                        ? (m.tenure_active
                            ? <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-bold inline-flex items-center gap-1"><CheckCircle2 className="w-2.5 h-2.5" /> Active tenure</span>
                            : <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 font-bold">Tenure ended</span>)
                        : <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 font-bold inline-flex items-center gap-1"><AlertCircle className="w-2.5 h-2.5" /> Update required</span>}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {openMember && <MemberEditor member={openMember} onClose={() => setOpenMember(null)} onSaved={() => { setOpenMember(null); load(); }} />}
      </div>
    </Layout>
  );
}

function MemberEditor({ member, onClose, onSaved }) {
  const [form, setForm] = useState({
    address: member.address || "",
    photo_url: member.photo_url || "",
    aadhaar_front_url: member.aadhaar_front_url || "",
    aadhaar_back_url: member.aadhaar_back_url || "",
    cv_url: member.cv_url || "",
    tenure_start: member.tenure_start || "",
    tenure_end: member.tenure_end || "",
  });
  const [saving, setSaving] = useState(false);

  const upload = async (key, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [key]: r.data.url }));
      toast.success("Uploaded");
    } catch (e) { toast.error("Upload failed"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/members/${member.user_id}`, form);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const downloadPdf = () => {
    const token = localStorage.getItem("munners_token");
    window.open(`${BACKEND_URL}/api/admin/members/${member.user_id}/pdf?t=${token}`, "_blank");
  };

  const Attach = ({ label, k, accept }) => (
    <div>
      <div className="text-[10px] font-bold uppercase text-slate-500 mb-1">{label}</div>
      {form[k] ? (
        <div className="flex items-center gap-2">
          <a href={form[k].startsWith("http") ? form[k] : `${BACKEND_URL}${form[k]}`} target="_blank" rel="noreferrer" className="text-xs text-orange-700 hover:underline inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> View</a>
          <button onClick={() => setForm({ ...form, [k]: "" })} className="text-rose-500 text-xs hover:underline">Remove</button>
        </div>
      ) : (
        <label className="btn-ghost text-xs cursor-pointer py-1.5 inline-flex" data-testid={`upload-${k}`}>
          <ImagePlus className="w-3 h-3" /> Upload
          <input type="file" accept={accept} className="hidden" onChange={(e) => upload(k, e.target.files?.[0])} />
        </label>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="member-editor">
        <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-2xl overflow-hidden bg-slate-100 flex items-center justify-center">
              {form.photo_url ? <img src={form.photo_url.startsWith("http") ? form.photo_url : `${BACKEND_URL}${form.photo_url}`} alt="" className="w-full h-full object-cover" /> : <UserIcon className="w-6 h-6 text-slate-300" />}
            </div>
            <div>
              <h3 className="font-display font-black text-lg">{member.name}</h3>
              <p className="text-xs text-slate-500">{member.email} · {ROLE_LABELS[member.role] || member.role}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><div className="text-[10px] uppercase font-bold text-slate-500">Phone</div><div className="mt-0.5 text-slate-800">{member.phone || "—"}</div></div>
            <div><div className="text-[10px] uppercase font-bold text-slate-500">Member ID</div><div className="mt-0.5 text-slate-800">{member.member_id || "—"}</div></div>
            <div><div className="text-[10px] uppercase font-bold text-slate-500">City</div><div className="mt-0.5 text-slate-800">{member.city || "—"}</div></div>
            <div><div className="text-[10px] uppercase font-bold text-slate-500">Region</div><div className="mt-0.5 text-slate-800">{member.region || "—"}</div></div>
          </div>

          <textarea rows={2} placeholder="Address" className="input-pill" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="member-address-input" />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Tenure start</div>
              <input type="date" className="input-pill" value={form.tenure_start} onChange={(e) => setForm({ ...form, tenure_start: e.target.value })} />
            </div>
            <div>
              <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Tenure end</div>
              <input type="date" className="input-pill" value={form.tenure_end} onChange={(e) => setForm({ ...form, tenure_end: e.target.value })} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-2 border-t border-slate-100">
            <Attach label="Passport photo" k="photo_url" accept="image/*" />
            <Attach label="CV / Resume" k="cv_url" accept=".pdf,.doc,.docx" />
            <Attach label="Aadhaar (front)" k="aadhaar_front_url" accept="image/*,.pdf" />
            <Attach label="Aadhaar (back)" k="aadhaar_back_url" accept="image/*,.pdf" />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
          <button onClick={downloadPdf} className="btn-ghost text-sm" data-testid="member-download-pdf"><Download className="w-4 h-4" /> Download PDF</button>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-ghost">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-50" data-testid="member-save">{saving ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
